import { Trophy, Award, Medal } from "lucide-react";

/**
 * Visual tournament bracket — 8 players → 4 L0 → 2 L1 → 1 L2 final → 1 L3 bronze
 * Renders horizontally on desktop, stacked on mobile.
 */
const STATUS_LABEL = {
  registration_open: { text: "REGISTRATION OPEN", color: "text-yellow-400" },
  active:            { text: "LIVE TOURNAMENT",    color: "text-[color:var(--kl-red)]" },
  completed:         { text: "COMPLETED",          color: "text-white/40" },
};

export default function TournamentBracket({ tournament, compact = false, onStartMatch }) {
  if (!tournament) return null;
  const b = tournament.bracket || {};
  const sl = STATUS_LABEL[tournament.status] || { text: "TOURNAMENT", color: "text-white/50" };
  const maxPlayers = tournament.max_players || 8;
  const currentCount = tournament.current_player_count ?? (tournament.players?.length ?? 0);
  return (
    <div className="w-full" data-testid="tournament-bracket">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <div className="flex items-center gap-3">
          <span className={`font-mono-kl text-[10px] uppercase tracking-[0.3em] ${sl.color}`}>
            <span className="kl-accent-line mr-3" />
            {sl.text}
          </span>
          {tournament.auto_created && (
            <span className="font-mono-kl text-[8px] uppercase tracking-widest text-yellow-400/60 border border-yellow-400/20 rounded px-1.5 py-0.5">Auto</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Capacity bar */}
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
              <div className="h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, (currentCount / maxPlayers) * 100)}%`, background: currentCount >= maxPlayers ? "var(--kl-red)" : "#facc15" }} />
            </div>
            <span className="font-mono-kl text-[9px] text-white/40">{currentCount}/{maxPlayers}</span>
          </div>
          <div className="font-display text-2xl md:text-3xl text-white uppercase tracking-tight">{tournament.name}</div>
        </div>
      </div>

      {/* podium */}
      {(tournament.winner || tournament.runner_up || tournament.third_place) && (
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="podium">
          <PodiumCell rank="01" icon={<Trophy className="w-4 h-4" />} label="Champion" player={tournament.winner} bg="rgba(230,57,70,0.18)" border="rgba(230,57,70,0.6)" />
          <PodiumCell rank="02" icon={<Award className="w-4 h-4" />} label="Runner-up" player={tournament.runner_up} bg="rgba(255,255,255,0.04)" border="rgba(255,255,255,0.15)" />
          <PodiumCell rank="03" icon={<Medal className="w-4 h-4" />} label="3rd Place" player={tournament.third_place} bg="rgba(255,209,102,0.08)" border="rgba(255,209,102,0.35)" />
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4 md:gap-6 items-stretch">
        {/* L0 — 4 matches */}
        <Column title="L0 · QUARTER" sub="4 battles">
          {b.L0?.map((s) => <MatchRow key={s.m} slot={s} level="L0" compact={compact} onStart={onStartMatch} />)}
        </Column>

        {/* L1 — 2 matches */}
        <Column title="L1 · SEMI" sub="random pair">
          {b.L1?.map((s) => <MatchRow key={s.m} slot={s} level="L1" compact={compact} onStart={onStartMatch} />)}
        </Column>

        {/* L2 — Final */}
        <Column title="L2 · ULTIMATE" sub="1st vs 2nd">
          {b.L2?.map((s) => <MatchRow key={s.m} slot={s} level="L2" compact={compact} onStart={onStartMatch} highlight />)}
        </Column>

        {/* L3 — Bronze */}
        <Column title="L3 · BRONZE" sub="for 3rd place">
          {b.L3?.map((s) => <MatchRow key={s.m} slot={s} level="L3" compact={compact} onStart={onStartMatch} />)}
        </Column>
      </div>
    </div>
  );
}

function Column({ title, sub, children }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="border-b border-white/10 pb-2">
        <div className="font-mono-kl text-[10px] uppercase tracking-[0.25em] text-white/50">{title}</div>
        <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/30">{sub}</div>
      </div>
      <div className="flex flex-col gap-3 flex-1">{children}</div>
    </div>
  );
}

const SLOT_STATUS_LABEL = {
  waiting_for_players: { text: "OPEN",    color: "text-yellow-400/60" },
  pending:             { text: "READY",   color: "text-yellow-400"    },
  active:              { text: "LIVE",    color: "text-[color:var(--kl-red)]" },
  done:                { text: "DONE",    color: "text-white/40"      },
};

function MatchRow({ slot, level, compact, onStart, highlight }) {
  const status = slot.status;
  const p1Name = slot.p1?.name || "TBD";
  const p2Name = slot.p2?.name || "TBD";
  const winnerPid = slot.winner_pid;
  const canStart = status === "pending";
  const sl = SLOT_STATUS_LABEL[status] || { text: "WAITING", color: "text-white/30" };

  return (
    <div
      className={`rounded-md border p-3 transition-colors ${status === "active" ? "match-active-glow" : ""}`}
      style={{
        borderColor: status === "done" && highlight ? "rgba(230,57,70,0.5)"
          : status === "active" ? "rgba(230,57,70,0.4)"
          : status === "pending" ? "rgba(250,204,21,0.3)"
          : "rgba(255,255,255,0.08)",
        background: status === "active" ? "rgba(230,57,70,0.10)"
          : status === "done" ? "rgba(255,255,255,0.03)"
          : status === "pending" ? "rgba(250,204,21,0.04)"
          : "rgba(255,255,255,0.02)",
      }}
      data-testid={`slot-${level}-${slot.m}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40">
          {level} · Match {slot.m}
        </span>
        <span className={`font-mono-kl text-[9px] uppercase tracking-widest ${sl.color}`}>
          {sl.text}
        </span>
      </div>
      <PlayerLine name={p1Name} score={slot.p1_total} winner={winnerPid && slot.p1_pid === winnerPid} avatar={slot.p1?.avatar} />
      <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30 my-0.5 text-center">VS</div>
      <PlayerLine name={p2Name} score={slot.p2_total} winner={winnerPid && slot.p2_pid === winnerPid} avatar={slot.p2?.avatar} />

      {onStart && canStart && !compact && (
        <button
          onClick={() => onStart(level, slot.m)}
          className="btn-submit w-full justify-center mt-3 py-2 text-xs"
          data-testid={`start-${level}-${slot.m}`}
        >
          ▶ Start Match
        </button>
      )}
      {onStart && status === "active" && slot.room_id && !compact && (
        <a
          href={`/battle?room=${slot.room_id}&t_level=${level}&t_match=${slot.m}`}
          className="btn-ghost-dark w-full justify-center mt-3 py-2 text-xs inline-block text-center"
          data-testid={`open-${level}-${slot.m}`}
        >
          Open Battle →
        </a>
      )}
    </div>
  );
}

function PlayerLine({ name, score, winner, avatar }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 min-w-0">
        {avatar?.img && (
          <div className="w-5 h-6 rounded overflow-hidden shrink-0" style={{ background: "rgba(255,255,255,0.05)" }}>
            <img src={avatar.img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
          </div>
        )}
        <span className={`font-display text-base md:text-lg uppercase truncate ${winner ? "text-[color:var(--kl-red)]" : "text-white"}`}>
          {name}
        </span>
      </div>
      <span className={`font-display text-base shrink-0 ${winner ? "text-[color:var(--kl-red)]" : "text-white/40"}`}>
        {score === null || score === undefined ? "—" : score}
      </span>
    </div>
  );
}

function PodiumCell({ rank, icon, label, player, bg, border }) {
  return (
    <div className="rounded-md border p-3" style={{ background: bg, borderColor: border }}>
      <div className="flex items-center gap-2 font-mono-kl text-[10px] uppercase tracking-widest text-white/60">
        {icon} {label} · {rank}
      </div>
      <div className="font-display text-xl md:text-2xl uppercase text-white mt-1 truncate">{player?.name || "—"}</div>
    </div>
  );
}
